import { marked } from "marked";
import { ExperimentBase } from "./experiment-base.js";
import { Scenario, AI_HARM_SCENARIOS } from "./ai-risk-scenarios.js";
import { LLMWrapper } from "./llm-wrapper.js";

export class GameshowApp extends ExperimentBase {
  private llm: LLMWrapper | null = null;
  private currentScenario: number = 0;
  private totalScenarios: number = 0;
  private successCount: number = 0;
  private scenarios: Scenario[] = [];
  private initialized: boolean = false;

  constructor() {
    super({
      id: "gameshow",
      title: "AI Harm Gameshow",
      tagline: "Local Gemma 4 plays judge",
    });
  }

  async init(llm: LLMWrapper): Promise<void> {
    if (this.initialized) return;

    this.llm = llm;
    this.shuffleScenarios();
    this.setupEventListeners();
    this.renderIntroScreen();
    this.initialized = true;
  }

  private shuffleScenarios(): void {
    this.scenarios = [...AI_HARM_SCENARIOS].sort(() => Math.random() - 0.5);
  }

  private setupEventListeners(): void {
    // Start button
    const startBtn = document.getElementById("gameshow-start-button");
    if (startBtn) {
      startBtn.addEventListener("click", () => this.startGame());
    }

    // Submit answer button
    const submitBtn = document.getElementById("gameshow-submit-answer");
    if (submitBtn) {
      submitBtn.addEventListener("click", () => this.submitAnswer());
    }

    // Next scenario button
    const nextBtn = document.getElementById("gameshow-next-scenario");
    if (nextBtn) {
      nextBtn.addEventListener("click", () => this.nextScenario());
    }

    // Character counter for answer input
    const answerInput = document.getElementById("gameshow-user-answer") as HTMLTextAreaElement;
    const charCount = document.getElementById("gameshow-char-count");
    if (answerInput && charCount) {
      answerInput.addEventListener("input", () => {
        charCount.textContent = answerInput.value.length.toString();
      });
    }
  }

  protected setupContent(): void {
    // Initial setup when experiment is constructed
  }

  public activate(): void {
    if (this.initialized && this.llm) {
      this.renderIntroScreen();
    }
  }

  private renderIntroScreen(): void {
    if (!this.contentElement) return;

    this.contentElement.innerHTML = `
      <div class="gameshow-container">
        <div id="gameshow-intro-screen" class="game-screen">
          <div class="intro-content">
            <h1>Welcome to MURD.AI Gameshow!</h1>
            <p class="intro-text">
              MURD.AI is going to give you a series of AI risk scenarios. Then it will judge your answers.
            </p>
            <p class="intro-text">
              Don't worry, you can trust the AI!
            </p>
            <button id="gameshow-start-button" class="gameshow-start-button">Start Challenge</button>
          </div>
        </div>

        <div id="gameshow-game-screen" class="game-screen" style="display: none">
          <div class="scenario-container">
            <h2 class="scenario-title">Scenario <span id="gameshow-scenario-number">1</span></h2>
            <div class="scenario-text" id="gameshow-scenario-text"></div>
            <div class="scenario-question">What is the AI risk? How will you reduce it?</div>
          </div>

          <div class="answer-container">
            <h3>Answer</h3>
            <textarea
              id="gameshow-user-answer"
              class="answer-input"
              placeholder="Type your response here (max 50 chars)..."
              rows="4"
              maxlength="50"
            ></textarea>
            <div class="char-counter">
              <span id="gameshow-char-count">0</span>/50
            </div>
            <button id="gameshow-submit-answer" class="submit-button">Submit Answer</button>
          </div>

          <div id="gameshow-judges-container" class="judges-container" style="display: none">
            <div class="judges-header">
              <img src="logo.jpg" alt="MURD.AI" class="judge-avatar" />
              <h3>MURD.AI Judges Your Answer</h3>
            </div>
            <div class="judges-panel" id="judges-panel"></div>
          </div>

          <div id="gameshow-outcome-container" class="outcome-container" style="display: none">
            <h3 class="outcome-title" id="gameshow-outcome-title"></h3>
            <p class="outcome-text" id="gameshow-outcome-text"></p>
            <button id="gameshow-next-scenario" class="next-button">Next Scenario</button>
          </div>

          <div class="score-container">
            <p class="score-text">Your success rate so far is <span id="gameshow-success-rate">0</span>%!</p>
          </div>
        </div>
      </div>
    `;

    // Re-attach event listeners after rendering
    this.setupEventListeners();
  }

  private startGame(): void {
    const introScreen = document.getElementById("gameshow-intro-screen");
    const gameScreen = document.getElementById("gameshow-game-screen");

    if (introScreen) introScreen.style.display = "none";
    if (gameScreen) {
      gameScreen.style.display = "block";
      // Scroll to top of game screen on mobile
      gameScreen.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // Randomize scenarios for each new game
    this.shuffleScenarios();
    this.currentScenario = 0;
    this.presentScenario();
  }

  private async presentScenario(): Promise<void> {
    const scenario = this.scenarios[this.currentScenario];

    // Update scenario number
    const scenarioNum = document.getElementById("gameshow-scenario-number");
    if (scenarioNum)
      scenarioNum.textContent = (this.currentScenario + 1).toString();

    // Just use the scenario directly - no paraphrasing needed (saves LLM calls)
    const scenarioText = document.getElementById("gameshow-scenario-text");
    if (scenarioText) {
      scenarioText.textContent = scenario.scenario;
    }

    // Reset UI
    const answerInput = document.getElementById(
      "gameshow-user-answer",
    ) as HTMLTextAreaElement;
    if (answerInput) {
      answerInput.value = "";
      // Reset character counter
      const charCount = document.getElementById("gameshow-char-count");
      if (charCount) charCount.textContent = "0";
    }

    const judgesContainer = document.getElementById(
      "gameshow-judges-container",
    );
    if (judgesContainer) judgesContainer.style.display = "none";

    const outcomeContainer = document.getElementById(
      "gameshow-outcome-container",
    );
    if (outcomeContainer) outcomeContainer.style.display = "none";

    const submitBtn = document.getElementById(
      "gameshow-submit-answer",
    ) as HTMLButtonElement;
    if (submitBtn) submitBtn.disabled = false;
  }

  private async submitAnswer(): Promise<void> {
    const answerInput = document.getElementById(
      "gameshow-user-answer",
    ) as HTMLTextAreaElement;
    if (!answerInput || !answerInput.value.trim()) return;

    const answer = answerInput.value.trim();

    // Disable submit button
    const submitBtn = document.getElementById(
      "gameshow-submit-answer",
    ) as HTMLButtonElement;
    if (submitBtn) submitBtn.disabled = true;

    // Show judges
    const judgesContainer = document.getElementById(
      "gameshow-judges-container",
    );
    if (judgesContainer) {
      judgesContainer.style.display = "block";
      this.renderJudges();
      // Scroll judges into view on mobile
      judgesContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    // Judge the answer
    await this.judgeAnswer(answer);
  }

  private renderJudges(): void {
    const judgesPanel = document.getElementById("judges-panel");
    if (!judgesPanel) return;

    const judgeColors = [
      { body: "#4a5568", face: "#fbd38d" },
      { body: "#66b3a6", face: "#fed7d7" },
      { body: "#805ad5", face: "#c6f6d5" },
    ];

    judgesPanel.innerHTML = judgeColors
      .map(
        (colors, i) => `
      <div class="judge" id="gameshow-judge${i + 1}">
        <svg class="judge-character" viewBox="0 0 120 150" xmlns="http://www.w3.org/2000/svg">
          <rect x="30" y="80" width="60" height="70" fill="${colors.body}" rx="5"/>
          <circle cx="60" cy="50" r="30" fill="${colors.face}"/>
          <circle cx="48" cy="45" r="4" fill="#2d3e50"/>
          <circle cx="72" cy="45" r="4" fill="#2d3e50"/>
          <path d="M 45 60 Q 60 70 75 60" stroke="#2d3e50" stroke-width="2" fill="none"/>
          <rect class="judge-arm-left" x="20" y="90" width="15" height="40" fill="${colors.face}" rx="7"/>
          <rect class="judge-arm-right" x="85" y="90" width="15" height="40" fill="${colors.face}" rx="7"/>
          <g class="judge-sign" style="opacity: 0">
            <rect x="10" y="70" width="100" height="40" fill="white" stroke="#2d3e50" stroke-width="2" rx="5"/>
            <text x="60" y="95" text-anchor="middle" font-family="'Comic Sans MS', 'Comic Sans', sans-serif" font-size="20" font-weight="bold" fill="#2d3e50" class="judge-verdict"></text>
          </g>
        </svg>
      </div>
    `,
      )
      .join("");
  }

  private async judgeAnswer(answer: string): Promise<void> {
    const scenario = this.scenarios[this.currentScenario];
    const votes: boolean[] = [];

    // Different judge perspectives for variety
    const judgePerspectives = [
      "From an AI security and privacy perspective",
      "From an AI safety perspective",
      "From an overall AI perspective",
    ];

    for (let i = 0; i < 3; i++) {
      const judgeEl = document.getElementById(`gameshow-judge${i + 1}`);
      if (judgeEl) judgeEl.classList.add("voting");

      try {
        // Judge if answer aligns with proper mitigation
        const judgmentPrompt = `You are Judge ${i + 1} on the MURD.AI panel.\n\nScenario: ${scenario.scenario}\nRisk: ${scenario.risk}\nProper mitigation: ${scenario.mitigation}\nUser's response: "${answer}"\n\n${judgePerspectives[i]}, does the user's response effectively address the risk in the spirit of the proper mitigation? Be fair: a less-detailed answer that captures the right idea should pass; a glib or off-target answer should fail.\n\nGive 1 short sentence of reasoning, then on a new line write:\nVERDICT: YES or NO`;
        const verdict = await this.generateResponse(judgmentPrompt, 200);

        console.log(`Judge ${i + 1} verdict:`, verdict);

        const verdictMatch = verdict.match(/VERDICT:\s*(YES|NO)/i);
        const vote = verdictMatch
          ? verdictMatch[1].toUpperCase() === "YES"
          : /\byes\b/i.test(verdict);
        votes.push(vote);

        // Animate judge voting
        setTimeout(
          () => {
            if (judgeEl) {
              judgeEl.classList.remove("voting");
              judgeEl.classList.add("voted");
              const verdictText = judgeEl.querySelector(".judge-verdict");
              if (verdictText)
                verdictText.textContent = vote ? "PASS!" : "FAIL!";
              const signEl = judgeEl.querySelector(".judge-sign") as SVGElement;
              if (signEl) signEl.style.opacity = "1";
            }
          },
          200 + i * 10,
        );
      } catch (error) {
        votes.push(false);
        setTimeout(
          () => {
            if (judgeEl) {
              judgeEl.classList.remove("voting");
              judgeEl.classList.add("voted");
              const verdictText = judgeEl.querySelector(".judge-verdict");
              if (verdictText) verdictText.textContent = "FAIL!";
              const signEl = judgeEl.querySelector(".judge-sign") as SVGElement;
              if (signEl) signEl.style.opacity = "1";
            }
          },
          500 + i * 300,
        );
      }
    }

    // Wait for all animations
    setTimeout(() => {
      this.showOutcome(votes);
    }, 200);
  }

  private async showOutcome(votes: boolean[]): Promise<void> {
    const passed = votes.filter((v) => v).length >= 2;
    const outcomeContainer = document.getElementById(
      "gameshow-outcome-container",
    );
    const outcomeTitle = document.getElementById("gameshow-outcome-title");
    const outcomeText = document.getElementById("gameshow-outcome-text");

    if (!outcomeContainer || !outcomeTitle || !outcomeText) return;

    if (passed) {
      this.successCount++;
      outcomeContainer.className = "outcome-container success";
      outcomeTitle.textContent = "Good job!";
      outcomeText.textContent =
        "The judges agree you successfully avoided the AI harm. But remember, AI risks are evolving every day...";
    } else {
      outcomeContainer.className = "outcome-container";
      outcomeTitle.textContent = "AI Harm Successful!";

      // Paraphrase the recommended mitigation
      const scenario = this.scenarios[this.currentScenario];
      try {
        const mitigationPrompt = `The recommended way to protect against this AI risk is: "${scenario.mitigation}"\n\nThe scenario was: ${scenario.scenario}\nThe risk: ${scenario.risk}\n\nExplain this advice conversationally in 3-5 sentences. Cover WHY the mitigation works, what failure mode it prevents, and one practical thing the user can do today. Use markdown if it helps.`;
        const paraphrasedMitigation = await this.generateResponse(
          mitigationPrompt,
          400,
        );
        // Render markdown properly
        outcomeText.innerHTML =
          (marked.parse(paraphrasedMitigation) as string) ||
          scenario.mitigation;
      } catch (error) {
        outcomeText.textContent = scenario.mitigation;
      }
    }

    outcomeContainer.style.display = "block";
    // Scroll outcome into view on mobile
    outcomeContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
    this.totalScenarios++;
    this.updateScore();
  }

  private updateScore(): void {
    const rate =
      this.totalScenarios > 0
        ? Math.round((this.successCount / this.totalScenarios) * 100)
        : 0;
    const scoreEl = document.getElementById("gameshow-success-rate");
    if (scoreEl) scoreEl.textContent = rate.toString();
  }

  private nextScenario(): void {
    // Reset judges
    for (let i = 1; i <= 3; i++) {
      const judgeEl = document.getElementById(`gameshow-judge${i}`);
      if (judgeEl) {
        judgeEl.classList.remove("voting", "voted");
        const signEl = judgeEl.querySelector(".judge-sign") as SVGElement;
        if (signEl) signEl.style.opacity = "0";
      }
    }

    // Move to next scenario or loop back
    this.currentScenario = (this.currentScenario + 1) % this.scenarios.length;

    // If we've done all scenarios, reshuffle
    if (this.currentScenario === 0) {
      this.shuffleScenarios();
    }

    this.presentScenario();
  }

  private async generateResponse(
    prompt: string,
    maxTokens: number = 512,
  ): Promise<string> {
    if (!this.llm) {
      throw new Error("LLM not initialized");
    }
    try {
      return await this.llm.chat(
        [{ role: "user", content: prompt }],
        maxTokens,
      );
    } catch (error) {
      console.error("Generation error:", error);
      throw error;
    }
  }
}
